import { FC, useEffect, useRef, useState } from "react";
import * as toast from "@app/network-outreach/export/export-panel-body/toast";
import * as helpers from "@app/network-outreach/export/export-panel-body/exportPanelBodyHelpers";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { OutreachDataFilterType, OutreachExportFormatType, OutreachExportType } from "@app/network-outreach/export/enumerations";
import { SelectDataFilter, SelectDateRangeFilter, SelectFormatFilter } from "@app/network-outreach/export/export-panel-body/filters";
import { OutreachReportPanelBodyProps } from "@app/network-outreach/export/export-panel-body/outreachReportPanelBodyBase";
import { ExportNetworkOutreachModel } from "@app/network-outreach/models";
import OutreachService from "@app/network-outreach/outreachService";
import GenerateBody from "@app/network-outreach/export/export-panel-body/generate-body/GenerateBody";

const formatReportName = (exportType: string) => {
  return exportType
    .split("-")
    .filter(x => x.length > 0)
    .map((x) => (x.charAt(0).toUpperCase() + x.slice(1)))
    .join("");
}

const OutreachDataPanelBody: FC<OutreachReportPanelBodyProps> = (props: OutreachReportPanelBodyProps) => {
  const exportServiceRef = useRef<OutreachService>(null);
  if (exportServiceRef.current === null) {
    exportServiceRef.current = new OutreachService();
  }
  const exportService = exportServiceRef.current;

  const [dataFilterSelection, setDataFilterSelection] = useState(OutreachDataFilterType.All);
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [lastQuarters, setLastQuarters] = useState<{ key: string; value: string }[]>([]);
  const [outputFormat, setOutputFormat] = useState(OutreachExportFormatType.CSV);
  const [reportName, setReportName] = useState(helpers.reportName(`NetworkOutreach_${formatReportName(props.activeExportType)}`));
  const [selectedQuarter, setSelectedQuarter] = useState("");

  useEffect(() => {
    const newLastQuarters: { key: string; value: string }[] = calculateQuarters();

    setIsLoading(false);
    setLastQuarters(newLastQuarters);
    setSelectedQuarter(history.state?.state?.currentQuarter ?? newLastQuarters[0].key);
  }, []);

  const handleDataSelectionChange = (dataSelection: OutreachDataFilterType) => {
    setDataFilterSelection(dataSelection);
    setIsLoading(false);
  }

  const handleOutputFormatChange = (newOutputFormat: OutreachExportFormatType) => {
    setIsLoading(false);
    setOutputFormat(newOutputFormat);
  }

  const handleSelectQuarterChange = (newSelectedQuarter: string) => {
    setIsLoading(false);
    setSelectedQuarter(newSelectedQuarter);
  }

  const handleGenerateClick = async () => {
    props.onGenerateClick();
    setIsLoading(true);

    await generateNetworkOutreachExport();
    toast.toastInfo(reportName, props.onToastLeave);
  }

  const handleEmailOnSuccessChange = (newEmailOnSuccess: boolean) => {
    setEmailOnSuccess(newEmailOnSuccess);
  }

  const handleReportNameChange = (newReportName: string) => {
    setReportName(newReportName);
  }

  const generateNetworkOutreachExport = async () => {
    const networkOutreachReportModel: ExportNetworkOutreachModel = {
      dataFilter: dataFilterSelection,
      eMailOnSuccess: emailOnSuccess,
      exportName: reportName,
      formatType: outputFormat,
      runAsynchronous: true,
      selectedQuarter: selectedQuarter
    };
    try {
      switch (props.activeExportType) {
        case OutreachExportType.PractitionerAtLocation:
          await exportService.postExportPractitionerAtLocationReport(networkOutreachReportModel);
          break;
        case OutreachExportType.Practitioner:
          await exportService.postExportPractitionerReport(networkOutreachReportModel);
          break;
        case OutreachExportType.ServiceLocation:
          await exportService.postExportServiceLocationReport(networkOutreachReportModel);
          break;
        default: return;
      }
    } catch {
      setError("An error happened while generating report");
    }
  }

  const calculateQuarters = () => {
    const minimumQuarter = history.state?.state?.minimumQuarter;
    const defaults: Array<{ key: string, value: string }> = [{ key: "90-Day", value: "90-Day" }];
    const currentDate = new Date();
    let currentQuarter = Math.floor(currentDate.getMonth() / 3 + 1);
    let currentYear = currentDate.getFullYear();

    //minimumQuarter format: 2023Q2
    const minQuarterPart = minimumQuarter ? Number(minimumQuarter.substring(minimumQuarter.length - 1)) : currentQuarter;
    const minYear = minimumQuarter ? Number(minimumQuarter.substring(0, 4)) : currentYear;

    let quarterRange = 4;

    if (minYear === currentYear)
      currentQuarter == minQuarterPart ? quarterRange = 1 : quarterRange = currentQuarter - minQuarterPart + 1;
    else if (currentYear - minYear === 1)
      quarterRange = Math.min(currentQuarter - minQuarterPart + 5, 4);

    for (let i = 0; i < quarterRange; i++) {
      defaults.push({
        key: `${currentYear}Q${currentQuarter}`,
        value: `Q${currentQuarter} ${currentYear}`
      });
      currentQuarter = currentQuarter - 1;
      if (currentQuarter == 0) {
        currentQuarter = 4;
        currentYear = currentYear - 1;
      }
    }

    return defaults;
  }

  const firstColumnFilters = (
    <div className="col-12 col-sm-3">
      <SelectDataFilter
        testIdPrefix="outreach-data"
        dataSelection={dataFilterSelection}
        onDataSelectionChange={handleDataSelectionChange}
      />
    </div>
  )

  const secondColumnFilters = (
    <div className="col-12 col-sm-3">
      <SelectDateRangeFilter
        testIdPrefix="outreach-data"
        selectedQuarter={selectedQuarter}
        onSelectQuarterChange={handleSelectQuarterChange}
        options={lastQuarters}
      />
    </div>
  )

  const thirdColumnFilters = (
    <div className="col-12 col-sm-3">
      <SelectFormatFilter
        testIdPrefix="outreach-data"
        outputFormat={outputFormat}
        onOutputFormatChange={handleOutputFormatChange} />
    </div >
  )

  return (
    <>
      <div className="row">
        {!!error && <QESAlert classNames="col-12" type="danger" persist={true}>{error}</QESAlert>}
      </div>
      <div className="row">
        {firstColumnFilters}
        {secondColumnFilters}
        {thirdColumnFilters}
      </div>
      <GenerateBody
        {...props}
        emailOnSuccess={emailOnSuccess}
        reportName={reportName}
        onGenerateClick={handleGenerateClick}
        onEmailOnSuccessChange={handleEmailOnSuccessChange}
        onReportNameChange={handleReportNameChange}
        isLoading={isLoading}
      />
    </>
  );
}

export default OutreachDataPanelBody;
